/**
 * The single button primitive for every app (BL-1711, ratifying the `Button`
 * row of the ADR 030 Canon: bidder's behaviour model, admin's variant
 * vocabulary).
 *
 * The merge collapses two from-scratch buttons that did the same job — admin's
 * `Button` (ten variants incl. the clerk console's on-dark trio, `isLoading`,
 * the `type="button"` default of BUG-1390) and bidder-web's `BidderButton`
 * (icon slots, a bare `link` variant, the tactile press, the tenant-brand
 * contrast pipeline of BL-710, and `href` rendering a link). Every behaviour
 * fix used to land twice or miss one app; it now lands here once.
 *
 * WHY a `dialect` object: the look is deliberately different per app (ADR 030
 * § 3b — admin's back-office tokens, the buyer site's pill CTAs and runtime
 * brand colour), and `sharing.md` § 1 caps a skin at a thin passthrough. So
 * the appearance is DATA the skin declares once: class strings per variant and
 * size, plus three optional injections for what `packages/ui` cannot import
 * itself —
 *   - `merge`: admin resolves conflicting utilities with its own
 *     type-scale-aware tailwind-merge (BL-1004, BL-1841);
 *   - `useVariantClass`: a hook, so admin can swap the destructive fill inside
 *     its phone sheet (BL-2403) without the core knowing about sheets;
 *   - `resolveBrand` and `Link`: bidder's `makeBrandColor()` and `next/link`,
 *     both app code a shared package must not depend on.
 * Same shape as `Card` (BL-1244), `Avatar` (BL-1701) and `Badge` (BL-1712).
 */
'use client';

import clsx from 'clsx';
import { forwardRef } from 'react';
import type {
    AnchorHTMLAttributes,
    ButtonHTMLAttributes,
    ComponentType,
    CSSProperties,
    ForwardedRef,
    ReactElement,
    ReactNode,
    RefAttributes,
} from 'react';
import { Spinner } from './Spinner';

/** What a dialect's brand resolver returns for one variant. */
export interface ButtonBrand {
    /** Inline style for the element (a runtime fill / text colour). */
    style?: CSSProperties;
    /** Spinner colours while loading, so it reads on the brand fill. */
    spinner?: { accentColor?: string; trackColor?: string };
}

/** Props the core hands a dialect's `Link`. */
export type ButtonLinkProps = AnchorHTMLAttributes<HTMLAnchorElement> & { href: string };

/**
 * Per-app appearance binding: class strings plus the injections a shared
 * package cannot import itself. Every field except `variants` / `sizes` is
 * optional, so a third app starts from bare structure.
 */
export interface ButtonDialect<V extends string = string, S extends string = string> {
    /** Structural classes on every non-bare button (layout, press, focus ring). */
    base?: string;
    /** Per-variant chrome. */
    variants: Record<V, string>;
    /** Per-size padding and type. */
    sizes: Record<S, string>;
    /** Corner radius when `pill` is off. Omit when `base` already carries one. */
    radius?: string;
    /** Corner radius when `pill` is on. */
    pillRadius?: string;
    /** Applied when `fullWidth` is on. */
    fullWidth?: string;
    /**
     * Applied while disabled or loading. Omit when `base` already styles
     * `:disabled` itself (bidder's `disabled:` utilities).
     */
    disabled?: string;
    /**
     * Variants that are typography, not button chrome (bidder's `link`): they
     * skip `base`, `sizes`, the radius and `disabled`, and take `bareBase`.
     */
    bareVariants?: readonly V[];
    /** Classes for a bare variant. */
    bareBase?: string;
    /** Sizes whose label is dropped while loading (admin's `icon`). */
    hideLabelWhileLoading?: readonly S[];
    /** Resolves the composed class string (admin's type-scale-aware twMerge). */
    merge?: (classes: string) => string;
    /**
     * A hook the core calls on every render; a non-undefined result replaces
     * the variant's class. Admin swaps the destructive fill inside a phone
     * sheet this way. Must be a stable function — it is a hook.
     */
    useVariantClass?: (variant: V) => string | undefined;
    /** Runtime brand styling for a variant (bidder's `makeBrandColor()`). */
    resolveBrand?: (variant: V, brandColor: string | undefined) => ButtonBrand | null;
    /** Rendered for `href` (bidder's `next/link`). Without it `href` is ignored. */
    Link?: ComponentType<ButtonLinkProps>;
}

/** Props for {@link Button}. */
export interface ButtonProps<V extends string = string, S extends string = string>
    extends ButtonHTMLAttributes<HTMLButtonElement> {
    /** Per-app appearance binding. See {@link ButtonDialect}. */
    dialect: ButtonDialect<V, S>;
    /** One of the dialect's variant names. */
    variant: V;
    /** One of the dialect's size names. */
    size: S;
    /** Spinner in the leading slot, `aria-busy`, interaction disabled. */
    isLoading?: boolean;
    /** Rendered before the label; hidden while loading. */
    leadingIcon?: ReactNode;
    /** Rendered after the label; hidden while loading. */
    trailingIcon?: ReactNode;
    /** Use the dialect's pill radius. */
    pill?: boolean;
    /** Stretch to the container width. */
    fullWidth?: boolean;
    /** Passed to the dialect's brand resolver. */
    brandColor?: string;
    /** Render the dialect's `Link` instead of a `<button>` (when enabled). */
    href?: string;
    children?: ReactNode;
}

/** The no-op stand-in when a dialect has no variant hook; keeps the hook count fixed. */
function noVariantClass(): undefined {
    return undefined;
}

function ButtonImpl<V extends string, S extends string>(
    {
        dialect,
        variant,
        size,
        isLoading = false,
        leadingIcon,
        trailingIcon,
        pill = false,
        fullWidth = false,
        brandColor,
        href,
        children,
        className,
        disabled,
        // BUG-1390: default to `button`, not the HTML default of `submit`. A
        // button composed into someone else's `<form>` must not submit it; a
        // caller that wants a submit says `type="submit"`.
        type = 'button',
        style: callerStyle,
        ...rest
    }: ButtonProps<V, S>,
    ref: ForwardedRef<HTMLButtonElement>,
) {
    // Hooks first, unconditionally (rules of hooks): the dialect's hook runs
    // on every render whichever branch below renders. Aliased to a `use…` name
    // so react-hooks/rules-of-hooks sees it as a hook and guards its position.
    const useDialectVariantClass = dialect.useVariantClass ?? noVariantClass;
    const variantOverride = useDialectVariantClass(variant);
    const isDisabled = Boolean(disabled) || isLoading;
    const isBare = dialect.bareVariants?.includes(variant) ?? false;
    const brand = dialect.resolveBrand?.(variant, brandColor) ?? null;

    const composed = isBare
        ? clsx(dialect.bareBase, fullWidth && dialect.fullWidth, className)
        : clsx(
              dialect.base,
              variantOverride ?? dialect.variants[variant],
              dialect.sizes[size],
              pill ? dialect.pillRadius : dialect.radius,
              fullWidth && dialect.fullWidth,
              isDisabled && dialect.disabled,
              className,
          );
    const classes = dialect.merge ? dialect.merge(composed) : composed;

    // A caller's `style` merges over the computed brand style — it must never
    // replace it (BUG-1641: a caller passing only `{ color }` erased the brand
    // fill). Caller keys still win, so a deliberate override works.
    const style: CSSProperties | undefined =
        brand?.style || callerStyle ? { ...brand?.style, ...callerStyle } : undefined;

    const hideLabel = isLoading && (dialect.hideLabelWhileLoading?.includes(size) ?? false);
    const content = (
        <>
            {isLoading ? (
                // `decorative`: the button carries `aria-busy` and its own label,
                // so a labelled spinner would prepend "Loading" to its name.
                <Spinner
                    size={16}
                    decorative
                    accentColor={brand?.spinner?.accentColor}
                    trackColor={brand?.spinner?.trackColor}
                />
            ) : (
                leadingIcon
            )}
            {!hideLabel && children}
            {!isLoading && trailingIcon}
        </>
    );

    // A disabled or loading link falls back to a real disabled button so the
    // affordance stays inert.
    const LinkComponent = dialect.Link;
    if (href && LinkComponent && !isDisabled) {
        // `rest` is typed for a <button>; the only structural difference from
        // an <a>'s attributes is the element inside each event handler, which
        // is runtime-irrelevant here.
        const linkProps = rest as unknown as AnchorHTMLAttributes<HTMLAnchorElement>;
        return (
            <LinkComponent href={href} className={classes} style={style} {...linkProps}>
                {content}
            </LinkComponent>
        );
    }

    return (
        <button
            ref={ref}
            type={type}
            className={classes}
            style={style}
            disabled={isDisabled}
            aria-busy={isLoading || undefined}
            {...rest}
        >
            {content}
        </button>
    );
}

/**
 * The shared button. Bind a dialect in a per-app skin; call sites use the skin.
 * A forwarded `ref` reaches the `<button>`; the `href` branch renders the
 * dialect's `Link` without it.
 *
 * @param props - {@link ButtonProps}
 * @returns A `<button>`, or the dialect's `Link` when `href` is set and enabled.
 */
export const Button = forwardRef(ButtonImpl) as <V extends string, S extends string>(
    props: ButtonProps<V, S> & RefAttributes<HTMLButtonElement>,
) => ReactElement | null;
